import { Trans } from '@lingui/react/macro';
import { StandardPartPriceBreak } from '@luminovo/api-contracts';
import { isPresent } from '@luminovo/commons';
import { colorSystem, Text } from '@luminovo/design-system';
import { formatDecimal, formatMonetaryValue } from '@luminovo/frontend-utils';
import { Box, styled, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { JSX } from 'react';

interface CompareLineItemPriceBreaksProps {
    priceBreaks: StandardPartPriceBreak[];
    selectedMoq?: number;
    selectedMpq?: number;
}

const StyledTableCell = styled(TableCell)({
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 8,
    paddingRight: 8,
    height: '100%',
    borderBottom: 'none',
    verticalAlign: 'top',
});

export function CompareLineItemPriceBreaks({
    priceBreaks,
    selectedMoq,
    selectedMpq,
}: CompareLineItemPriceBreaksProps): JSX.Element | null {
    if (!priceBreaks || priceBreaks.length === 0) {
        return null;
    }

    const sortedPriceBreaks = [...priceBreaks].sort((a, b) => a.minimum_order_quantity - b.minimum_order_quantity);

    const isHighlighted = (priceBreak: StandardPartPriceBreak): boolean => {
        if (!isPresent(selectedMoq) || !isPresent(selectedMpq)) {
            return false;
        }
        return (
            priceBreak.minimum_order_quantity === selectedMoq && priceBreak.minimum_packaging_quantity === selectedMpq
        );
    };

    return (
        <Box
            sx={{
                border: `1px solid ${colorSystem.neutral[2]}`,
                borderRadius: '4px',
                overflow: 'hidden',
                backgroundColor: colorSystem.neutral.white,
            }}
        >
            <Table size="small">
                <TableHead sx={{ backgroundColor: colorSystem.neutral[1] }}>
                    <TableRow>
                        <StyledTableCell>
                            <Text variant="body-small-semibold" color={colorSystem.neutral[7]}>
                                <Trans>MOQ</Trans>
                            </Text>
                        </StyledTableCell>
                        <StyledTableCell>
                            <Text variant="body-small-semibold" color={colorSystem.neutral[7]}>
                                <Trans>Unit price</Trans>
                            </Text>
                        </StyledTableCell>
                        <StyledTableCell>
                            <Text variant="body-small-semibold" color={colorSystem.neutral[7]}>
                                <Trans>MPQ</Trans>
                            </Text>
                        </StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedPriceBreaks.map((priceBreak, idx) => {
                        const highlighted = isHighlighted(priceBreak);
                        const color = highlighted ? colorSystem.primary[6] : colorSystem.neutral[9];

                        return (
                            <TableRow key={idx} sx={{ backgroundColor: colorSystem.neutral.white }}>
                                <StyledTableCell>
                                    <Text
                                        variant={highlighted ? 'body-small-semibold' : 'body-small'}
                                        color={color}
                                        tabularNums
                                    >
                                        {formatDecimal(priceBreak.minimum_order_quantity)}
                                    </Text>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Text
                                        variant={highlighted ? 'body-small-semibold' : 'body-small'}
                                        color={color}
                                        tabularNums
                                    >
                                        {formatMonetaryValue(priceBreak.unit_price, 'unit-price')}
                                    </Text>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Text
                                        variant={highlighted ? 'body-small-semibold' : 'body-small'}
                                        color={color}
                                        tabularNums
                                    >
                                        {formatDecimal(priceBreak.minimum_packaging_quantity)}
                                    </Text>
                                </StyledTableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
}
